import {
  type Component,
  getCurrentInstance,
  type WatchCallback,
  type WatchOptions,
  type WatchSource,
  type WatchStopHandle,
  watch
} from 'vue'

export * from './age'
export * from './copy'
export * from './day-key'
export * from './display-url'

export const isSSR = typeof window === 'undefined'

type Dictionary = Record<string, unknown>

const isObject = (value: unknown): value is Dictionary => typeof value === 'object' && value !== null && !Array.isArray(value)

// The lockup stresses the middle part; the three spell APP_NAME.
export const APP_NAME_PARTS = ['Mar', 'ad', 'hon'] as const

export const APP_NAME = APP_NAME_PARTS.join('')

export const escapeHtml = (value: string) =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

const isEqualArray = (a: unknown, b: unknown) =>
  Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((value, index) => Object.is(value, b[index]))

export const mergeDeep = <T>(target: T, source: unknown): T => {
  if (!isObject(source)) return target
  if (!isObject(target)) return source as T

  let output: Dictionary | undefined
  for (const key of Object.keys(source)) {
    const previous = target[key]
    const value = source[key]
    const next = isObject(value) ? mergeDeep(previous, value) : isEqualArray(previous, value) ? previous : value
    if (!Object.is(next, previous)) {
      output ??= { ...target }
      output[key] = next
    }
  }
  return (output ?? target) as T
}

export const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

export const debounce = <A extends unknown[]>(fn: (...args: A) => void, ms = 100) => {
  let timeout: ReturnType<typeof setTimeout>
  return (...args: A) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => fn(...args), ms)
  }
}

type MapSources<T, Immediate> = {
  [K in keyof T]: T[K] extends WatchSource<infer V>
    ? Immediate extends true
      ? V | undefined
      : V
    : T[K] extends object
      ? Immediate extends true
        ? T[K] | undefined
        : T[K]
      : never
}

type DebounceWatchOptions<Immediate extends Readonly<boolean>> = WatchOptions<Immediate> & { delay?: number }

export function debounceWatch<T extends (WatchSource<unknown> | object)[], Immediate extends Readonly<boolean> = false>(
  sources: [...T],
  cb: WatchCallback<MapSources<T, false>, MapSources<T, Immediate>>,
  options?: DebounceWatchOptions<Immediate>
): WatchStopHandle

export function debounceWatch<T, Immediate extends Readonly<boolean> = false>(
  source: WatchSource<T>,
  cb: WatchCallback<T, Immediate extends true ? T | undefined : T>,
  options?: DebounceWatchOptions<Immediate>
): WatchStopHandle

export function debounceWatch(
  sources: WatchSource<unknown> | object | (WatchSource<unknown> | object)[],
  cb: WatchCallback,
  options?: DebounceWatchOptions<boolean>
) {
  return watch(sources as WatchSource<unknown>, debounce(cb as (...args: unknown[]) => void, options?.delay ?? 100), options)
}

type ComponentLookup = (name: string) => Component | undefined

export const findComponent = (lookup: ComponentLookup, name: string, uid?: string) => {
  const component = (uid && lookup(`${name}-${uid}`)) || lookup(name)
  if (!component) console.warn(`No component registered for name: ${name}${uid ? `, uid: ${uid}` : ''}`)
  return component
}

export const getComponent = (name: string, uid?: string) => {
  const instance = getCurrentInstance()
  if (!instance) {
    console.warn('getComponent must be called during setup() or render')
    return
  }
  return findComponent(key => instance.appContext.components[key], name, uid)
}
